import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Permission } from '../_models/permission';
import { Store } from '../_models/store';
import { PermissionService } from '../_services/permission.service';
import { StoreService } from '../_services/store.service';
import { UnreadService } from '../_services/unread-message.service';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { SwUpdate } from '@angular/service-worker';
import { environment } from '../../environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Capacitor } from '@capacitor/core';
import { MenuPhotosService } from '../_services/menu-photos.service';
import { FleetAccessService } from '../devices/fleet-access.service';
import { Router, NavigationEnd } from '@angular/router';
import { NAV_ITEMS, savedNavOrder, uiOrderIndex } from '../_models/ui-order';

@Component({
  selector: 'app-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss']
})
export class SideNavComponent implements OnInit, OnDestroy {
  store: Store;
  unreadCounter;
  permission: Permission;
  @Input() opened: boolean;
  private destroySubject: Subject<void> = new Subject<void>();
  currentVersion: string = '';
  latestVersion: string = '';
  isLatestVersion: boolean = false;
  isWeb: boolean = Capacitor.getPlatform() === 'web';

  /** Beta gate: in production, scheduling is only enabled for the
   *  "PRODUCTION DEMO" store.  Dev/staging show it for every store. */
  get schedulingAllowedForStore(): boolean {
    const PROD_API_HOSTS = ['backend.text-order.com', 'production.labrador.ai'];
    const apiUrl = (environment.apiUrl || '').toLowerCase();
    const isProdApi = PROD_API_HOSTS.some(h => apiUrl.includes(h));
    if (!isProdApi) return true;
    return (this.store?.store_name || '').toUpperCase().includes('PRODUCTION DEMO');
  }

  // ENG-5687: the merchant's side-nav order (store_data.nav_order), [] when the
  // configurable_nav_order flag is off or nothing was saved. Each link carries
  // `[style.order]`, which only takes effect once the list is a flex column
  // (.nav-list--ordered), so with no custom order the markup renders exactly as before.
  private navOrder: string[] = [];
  private navBuiltIn: { [key: string]: number } = NAV_ITEMS
    .reduce((acc, item, i) => { acc[item.key] = i; return acc; }, {} as { [key: string]: number });

  get customNavOrder(): boolean {
    return this.navOrder.length > 0;
  }

  /** Flex order for a nav link: saved position first, built-in position for the rest. */
  navOrderIndex(key: string): number {
    const builtIn = key in this.navBuiltIn ? this.navBuiltIn[key] : NAV_ITEMS.length;
    return uiOrderIndex(this.navOrder, key, builtIn);
  }

  constructor(private storeService: StoreService,
    private unreadService: UnreadService,
    private permissionService: PermissionService,
    private http: HttpClient, private updates: SwUpdate,
    private snackBar: MatSnackBar,
    private menuPhotosService: MenuPhotosService,
    private router: Router,
    public fleetAccess: FleetAccessService) { }

  /** Photo-completeness stats for the "Menu Photos" badge (populated once the dashboard card or module loads) */
  get menuPhotoStats() {
    return this.menuPhotosService.stats;
  }

    ngOnInit() {
      this.storeService.current.pipe(takeUntil(this.destroySubject)).subscribe(store => {
        this.store = Object.assign(new Store(), store);
        this.navOrder = savedNavOrder(this.store);
        if (this.store) {
          this.unreadService.current.pipe(takeUntil(this.destroySubject)).subscribe(counter => this.unreadCounter = counter);
        }
        // Re-apply dark mode once the store (and therefore its feature flags) has arrived.
        //
        // `storeService.current` is a BehaviorSubject seeded with null, so on a HARD RELOAD
        // straight onto /home/orders the NavigationEnd below fires BEFORE the store request
        // resolves. applyDarkMode() then read `this.store?.feature_flag['dark_mode']` as
        // undefined, took its else branch and REMOVED the classes — the Orders module loaded
        // in light mode. Switching to another tab and back masked it, because by then the
        // store was loaded and a fresh NavigationEnd re-evaluated the flag.
        //
        // Navigation alone is not enough: the flag has two independent inputs (the current
        // URL and the store), so re-evaluate whenever either one changes.
        this.applyDarkMode(this.router.url);
      });
    
      this.permissionService.current.pipe(takeUntil(this.destroySubject)).subscribe(permission => this.permission = permission);
    
      if (this.isWeb) {                    // <--- guard for web only
        this.loadCurrentVersionAndLatest();
      }

      // Toggle dark mode classes AFTER navigation completes (not on click)
      // to prevent other tabs from flashing dark during route transition
      this.router.events.pipe(
        filter(e => e instanceof NavigationEnd),
        takeUntil(this.destroySubject)
      ).subscribe((e: NavigationEnd) => {
        this.applyDarkMode(e.urlAfterRedirects || e.url);
      });
    }

  ngAfterViewInit() {

    // this.loadCurrentVersion();
    // this.loadLatestAppVersion();
  }

  ngOnDestroy() {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  loadLatestAppVersion(): void {
    const envID = environment.apiUrl == 'https://backend.text-order.com' ? 1 :
      environment.apiUrl == 'https://beta.labrador.ai' ? 2 :
      environment.apiUrl == 'https://pre-prod-order-api-backend-628090102143.us-east1.run.app' ? 2 :
      environment.apiUrl == 'https://api.beta.labrador.ai' ? 2 : 3;

    this.storeService.getLatestAppVersion(envID, 'business')
      .pipe(takeUntil(this.destroySubject))
      .subscribe(
        (data: any) => {
          this.latestVersion = data.version;
          this.isLatestVersion = this.currentVersion === this.latestVersion;

          if (!this.isLatestVersion) {
            if (this.isWeb) {
              this.snackBar.open('A new version is available. Please refresh.', 'Update', {
                duration: 10000,
                verticalPosition: 'top',
                horizontalPosition: 'center'
              }).onAction().subscribe(() => {
                this.updateAppVersion();
              });
            }
          } else {
            if (this.isWeb) {
              setTimeout(() => {
                this.snackBar.open('Your software is up to date.', 'OK', {
                  duration: 10000,
                  verticalPosition: 'top',
                  horizontalPosition: 'center'
                });
              }, 200);
            }
          }
        },
        (error) => {
          console.log('Version fetch skipped:', error?.message || 'unavailable');
        }
      );
  }

  async updateAppVersion(): Promise<void> {
    // Without a service worker there is nothing to activate, so a plain reload
    // is all we can do.
    if (!this.updates.isEnabled) {
      window.location.reload();
      return;
    }

    try {
      // The worker can only activate a version it has already downloaded, and
      // its own poll may not have run yet — so ask it to look first. Without
      // this, activateUpdate() resolves false on a freshly-loaded page and the
      // click appears to do nothing.
      await this.updates.checkForUpdate();

      if (!await this.updates.activateUpdate()) {
        // Nothing newer was cached. Reload anyway: the served bundle may well
        // be newer than what this tab started with.
        window.location.reload();
        return;
      }

      // Only now is the new version live in the worker, so reloading picks it
      // up. Reloading before this point re-served the old cached bundle —
      // which is what an unconditional reload at the end of this method used
      // to do, making the button look broken.
      window.location.reload();
    } catch (error) {
      console.error('Error activating update:', error);
      this.snackBar.open('Update failed. Please try again.', 'OK', {
        duration: 4000
      });
    }
  }

  loadCurrentVersionAndLatest() {
    this.http.get<{ version: string }>(`assets/version.json?v=${Date.now()}`).subscribe(
      (data) => {
        this.currentVersion = data.version;
        this.loadLatestAppVersion();
      },
      (error) => {
        console.log('Local version check skipped:', error?.message || 'unavailable');
      }
    );
  }

  onOrdersClick(url: string): void {
    // Dark mode class toggling moved to router NavigationEnd subscription
    // to prevent non-orders tabs from flashing dark during route transition
  }

  private applyDarkMode(url: string): void {
    const sidenavContent = document.querySelector('mat-sidenav-content .basic-container.smooth-scroll');

    // On a hard reload this can run before <mat-sidenav-content> exists. Bailing out
    // silently would leave the Orders module light until the next navigation, so retry
    // once the view has been created.
    if (!sidenavContent) {
      if (url.startsWith('/home/orders') && this.store?.feature_flag?.['dark_mode']) {
        setTimeout(() => this.applyDarkMode(url));
      }
      return;
    }

    // `feature_flag` is optional-chained: the store subject is seeded with null, and
    // Object.assign(new Store(), null) yields a Store with no feature_flag at all.
    if (url.startsWith('/home/orders') && this.store?.feature_flag?.['dark_mode']) {
      sidenavContent.classList.add('color-scheme-dark');
      document.body.classList.add('dark-mode-active');
    } else {
      sidenavContent.classList.remove('color-scheme-dark');
      document.body.classList.remove('dark-mode-active');
    }
  }

}
